import { useEffect, useRef, useState } from 'react';
import { motion, useInView } from 'framer-motion';

/**
 * ⚙️ EDIT THESE NUMBERS WHEN SCALE CHANGES
 * Keep them operator-truthful — easier to update upward than to retract.
 */
const METRICS = [
  { label: 'Active Implementations', value: 2, suffix: '', code: '01' },
  { label: 'Production Systems', value: 4, suffix: '', code: '02' },
  { label: 'Learners Reached', value: 5, suffix: 'K+', code: '03' },
  { label: 'On-time Delivery', value: 100, suffix: '%', code: '04' },
];

function CountUp({ to, suffix = '', duration = 1400 }) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: '-50px' });
  const [n, setN] = useState(0);

  useEffect(() => {
    if (!inView) return;
    const start = performance.now();
    let frame;
    const tick = (t) => {
      const elapsed = t - start;
      const progress = Math.min(elapsed / duration, 1);
      // ease-out-cubic
      const eased = 1 - Math.pow(1 - progress, 3);
      setN(Math.round(to * eased));
      if (progress < 1) frame = requestAnimationFrame(tick);
    };
    frame = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(frame);
  }, [inView, to, duration]);

  return (
    <span ref={ref} className="metric-number text-4xl md:text-5xl lg:text-6xl text-stark">
      {n}
      <span className="text-signal">{suffix}</span>
    </span>
  );
}

export function MetricsBar() {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: '-80px' });

  return (
    <section
      ref={ref}
      className="relative py-16 md:py-20 border-y border-line-1 bg-ink-2 overflow-hidden"
      data-testid="metrics-bar"
      aria-label="Operating metrics"
    >
      {/* Background data grid */}
      <div className="absolute inset-0 strategic-grid opacity-30 pointer-events-none" />

      <div className="relative max-w-[1400px] mx-auto px-6 md:px-12">
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
          className="mb-10"
        >
          <p className="eyebrow section-eyebrow-line">Live System Telemetry</p>
        </motion.div>

        <div
          className="grid grid-cols-2 lg:grid-cols-4 gap-px bg-line-1 border border-line-1 rounded-md overflow-hidden"
          role="list"
        >
          {METRICS.map((m, i) => (
            <motion.div
              key={m.label}
              initial={{ opacity: 0, y: 20 }}
              animate={inView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.5, delay: i * 0.08, ease: [0.16, 1, 0.3, 1] }}
              className="bg-surface hover:bg-surface-2 transition-colors duration-300 p-6 md:p-8 relative"
              data-testid={`metric-${m.code}`}
              role="listitem"
            >
              <span className="eyebrow text-stark-3 mb-3 block">M.{m.code}</span>
              <div className="leading-none mb-3">
                <CountUp to={m.value} suffix={m.suffix} />
              </div>
              <p className="text-sm md:text-base text-stark-2 leading-snug">
                {m.label}
              </p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}

export default MetricsBar;
